import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';
import authApi from '../api/authApi';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY_FIREBASE,
  authDomain: process.env.REACT_APP_BASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_BASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_BASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_BASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_BASE_APP_ID,
  measurementId: process.env.REACT_APP_BASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth();
export const db = initializeFirestore(app, {
  // Avoid unstable QUIC/WebChannel behavior on some networks/browsers.
  experimentalForceLongPolling: true,
  useFetchStreams: false,
});
export const storage = getStorage(app);

const isSupportedBrowser = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

const isDevelopMode = () => {
  return String(process.env.REACT_APP_DEVELOP).toLowerCase() === 'true';
};

export const messaging = isSupportedBrowser() && !isDevelopMode() ? getMessaging(app) : null;

const SW_REGISTER_TIMEOUT_MS = 10000;
const GET_TOKEN_TIMEOUT_MS = 10000;
const LOGIN_TOKEN_TIMEOUT_MS = 3000;

/**
 * Resolves to undefined instead of hanging or throwing when a Firebase call
 * stalls. Service worker registration and token generation have no built-in
 * deadline, so without this a slow network could block whatever awaited them
 * (CRCN-832).
 */
const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise((resolve) =>
      setTimeout(() => {
        console.warn(`[FCM] ${label} timed out after ${ms}ms`);
        resolve(undefined);
      }, ms),
    ),
  ]);

const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const swConfigParams = new URLSearchParams({
        apiKey: process.env.REACT_APP_API_KEY_FIREBASE ?? '',
        authDomain: process.env.REACT_APP_BASE_AUTH_DOMAIN ?? '',
        projectId: process.env.REACT_APP_BASE_PROJECT_ID ?? '',
        storageBucket: process.env.REACT_APP_BASE_STORAGE_BUCKET ?? '',
        messagingSenderId: process.env.REACT_APP_BASE_MESSAGING_SENDER_ID ?? '',
        appId: process.env.REACT_APP_BASE_APP_ID ?? '',
      });
      const registration = await navigator.serviceWorker.register(
        `/firebase-messaging-sw.js?${swConfigParams.toString()}`,
      );
      console.log('Service Worker registered with scope:', registration?.scope);
      return registration;
    } catch (error) {
      console.error('ServiceWorker registration failed: ', error);
    }
  }
};

/**
 * Returns an FCM token when notification permission is already granted.
 *
 * This never asks for permission: the browser prompt is requested only by the
 * in-app notification gate, after login and navigation have completed. Asking
 * here used to stall the login API whenever the prompt was left unanswered,
 * because `Notification.requestPermission()` never settles in that case
 * (CRCN-832). Always resolves, never throws.
 */
export const generateToken = async () => {
  if (!isSupportedBrowser() || isDevelopMode() || !messaging) return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  try {
    const registration = await withTimeout(
      registerServiceWorker(),
      SW_REGISTER_TIMEOUT_MS,
      'service worker registration',
    );
    return await withTimeout(
      getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      }),
      GET_TOKEN_TIMEOUT_MS,
      'token generation',
    );
  } catch (e) {
    console.error('FCM token generation failed: ', e);
  }
};

/**
 * Best-effort token for the login request payload.
 *
 * Bounded far more tightly than `generateToken` so the login API is never held
 * up by Firebase. When it times out the login still proceeds with an empty
 * token, and the real token is sent straight after by the notification gate or
 * the app bootstrap (CRCN-832).
 */
export const getFcmTokenForLogin = async () =>
  (await withTimeout(generateToken(), LOGIN_TOKEN_TIMEOUT_MS, 'login token lookup')) ?? '';

export const ensureFirebaseSignedIn = async () => {
  if (auth.currentUser) return auth.currentUser;
  try {
    const res = await authApi.getFirebaseCustomToken();
    const token = res?.data?.data?.firebaseCustomToken;
    if (!token) return null;
    const credential = await signInWithCustomToken(auth, token);
    return credential.user;
  } catch (e) {
    console.error('ensureFirebaseSignedIn failed: ', e);
    return null;
  }
};

export const revokeToken = async () => {
  if (!isSupportedBrowser() || isDevelopMode() || !messaging) return;
  const registration = await withTimeout(
    registerServiceWorker(),
    SW_REGISTER_TIMEOUT_MS,
    'service worker registration',
  );
  try {
    const currentToken = await withTimeout(
      getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      }),
      GET_TOKEN_TIMEOUT_MS,
      'token generation',
    );
    if (!currentToken) return;
    await deleteToken(messaging);
    return currentToken;
  } catch (e) {
    console.error('FCM token revoke failed: ', e);
  }
};
